import RoisPhotoPermissions from '../../../../plugins/roisPhotoPermissions';
import { isNativeApp } from '../../../../utils/positionSource';

/**
 * Qué puede hacer el operario cuando la cámara no tiene permiso en la APK:
 *  - `ask`: Android todavía enseña el diálogo → «Continuar» lo vuelve a pedir.
 *  - `settings`: ya no lo enseña (dos negativas) → solo queda Ajustes.
 *
 * Medido en la W el 25-09-2026: tras dos «No permitir», la cámara decía
 * «Permisos denegados» sin diálogo y sin decir cómo salir de ahí.
 */
export type CameraDenial = 'ask' | 'settings';

/**
 * Tras un `NotAllowedError`, qué ofrecer. `null` en el navegador (ahí el
 * permiso se cambia en el candado y el mensaje de siempre sirve) y si resulta
 * que el permiso sí está.
 *
 * Una APK vieja no tiene `cameraAccess`: entonces `settings`, que siempre
 * funciona.
 */
export async function cameraDenial(): Promise<CameraDenial | null> {
  if (!isNativeApp()) return null;
  try {
    const { granted, canAsk } = await RoisPhotoPermissions.cameraAccess();
    if (granted) return null;
    return canAsk ? 'ask' : 'settings';
  } catch {
    return 'settings';
  }
}

/**
 * ¿Ya hay permiso de cámara? Solo consulta. Es lo que se mira al volver de
 * Ajustes: 🔴 PEDIR desde el `resume` sería un bucle, porque con el permiso
 * negado para siempre Android contesta al instante pero la app pasa igual por
 * pausa y vuelta.
 */
export async function cameraGranted(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    return (await RoisPhotoPermissions.cameraAccess()).granted;
  } catch {
    return false;
  }
}

/** El error de `getUserMedia` es de permiso (y no de cámara ocupada, etc.). */
export function isPermissionError(err: unknown): boolean {
  const name = (err as { name?: string } | undefined)?.name;
  return name === 'NotAllowedError' || name === 'PermissionDeniedError';
}
